import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Grid,
  Typography,
  Button,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Stack,
  Paper,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  Zoom,
  Fade,
  Slide,
  IconButton,
  Tooltip,
  Avatar,
  LinearProgress,
  Autocomplete,
  CircularProgress as MuiCircularProgress,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  Lightbulb as LightbulbIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import useAuth from '../../../../Hooks/use-auth';
import client from '../../../../API';

const usageDurations = [
  { value: '30', label: '30 days', icon: '📅' },
  { value: '60', label: '60 days', icon: '📆' },
  { value: '90', label: '90 days', icon: '🗓️' },
  { value: '180', label: '180 days', icon: '📊' },
  { value: '360', label: '360 days', icon: '📈' },
];

const parseStructuredBrief = (brief = '') => {
  if (!brief) {
    return { creativeBrief: '', usage: '' };
  }

  const creativeMatch = brief.match(/# Creative Brief\s*([\s\S]*?)\s*# Usage Requirements/i);
  const usageMatch = brief.match(/Usage Duration:\s*([^\n\r]+)/i);

  return {
    creativeBrief: creativeMatch?.[1]?.trim() || '',
    usage: usageMatch?.[1]?.trim() || '',
  };
};

const normalizeTeamName = (name = '') => name.toLowerCase().replace(/\s+/g, ' ').trim();

const collectRoleEligibleIds = (teams = []) => {
  const accountManagerIds = new Set();
  const campaignManagerIds = new Set();
  const teamMembersById = new Map();

  teams.forEach((team) => {
    const normalizedName = normalizeTeamName(team?.name);
    const isAccountManagersTeam = normalizedName.includes('account manager');
    const isCampaignManagersTeam = normalizedName.includes('campaign manager');
    if (!isAccountManagersTeam && !isCampaignManagersTeam) return;

    const targetSet = isAccountManagersTeam ? accountManagerIds : campaignManagerIds;
    (team?.member_ids || []).forEach((id) => targetSet.add(id));
    (team?.members || []).forEach((member) => {
      if (!member?.id) return;
      targetSet.add(member.id);
      teamMembersById.set(member.id, member);
    });
    if (team?.team_leader_id) targetSet.add(team.team_leader_id);
    if (team?.team_leader?.id) {
      targetSet.add(team.team_leader.id);
      teamMembersById.set(team.team_leader.id, team.team_leader);
    }
  });

  return { accountManagerIds, campaignManagerIds, teamMembersById };
};

const buildManagerRoles = (user, accountManagerIds, campaignManagerIds) => {
  const roles = [];
  if (accountManagerIds.has(user?.id)) roles.push('AM');
  if (campaignManagerIds.has(user?.id)) roles.push('CM');
  if ((user?.type || '').toLowerCase() === 'admin') roles.push('ADMIN');
  return roles;
};

// ── Company Search Autocomplete ──────────────────────────────────────────────
function CompanySearchField({ value, onChange }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCompanies = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }
    setLoading(true);
    try {
      // Uses your existing client.companies.list() — filter client-side by name
      const result = await client.companies.list({ limit: 500, offset: 0 });
      const arr = Array.isArray(result) ? result : result?.companies || [];
      const filtered = arr.filter((c) =>
        c.name?.toLowerCase().includes(query.toLowerCase())
      );
      setOptions(filtered.slice(0, 20));
    } catch (e) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => fetchCompanies(inputValue), 300);
    return () => clearTimeout(timer);
  }, [inputValue, fetchCompanies]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      options={options}
      getOptionLabel={(option) => option?.name || ''}
      isOptionEqualToValue={(option, val) => option?.id === val?.id}
      loading={loading}
      filterOptions={(x) => x} // server-filtered
      noOptionsText={inputValue.length < 2 ? 'Type at least 2 characters…' : 'No companies found'}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Avatar
            src={option.phphref || ''}
            alt={option.name}
            sx={{ width: 32, height: 32, mr: 1.5, bgcolor: '#667eea', fontSize: 14 }}
          >
            {option.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
            {option.account_status && (
              <Typography variant="caption" color="text.secondary">
                {option.account_status}
                {option.blitzpay_account ? ' · BlitzPay' : ''}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Client Company"
          placeholder="Search companies…"
          required
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <MuiCircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: 'white',
              fontSize: '1rem',
              transition: 'all 0.2s ease',
              '&:hover': { backgroundColor: 'rgba(103, 126, 234, 0.02)' },
              '&.Mui-focused': {
                backgroundColor: 'rgba(103, 126, 234, 0.05)',
                '& fieldset': { borderColor: '#667eea', borderWidth: '2px' },
              },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' },
          }}
        />
      )}
      sx={{ width: '100%' }}
    />
  );
}
// ─────────────────────────────────────────────────────────────────────────────

const CampaignDetails = ({ onNext, onBack, setCampaignData, campaignData, prefilterEmail }) => {
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const userCompany = currentUser?.company_name || '';

  // Determine if the user is admin
  const isAdmin = (currentUser?.type || '').toLowerCase() === 'admin';

  const savedRequirements = parseStructuredBrief(campaignData?.campaignBrief);

  const [campaignName, setCampaignName] = useState(campaignData?.campaignName || '');
  const [emailRecipient, setEmailRecipient] = useState(
    campaignData?.emailRecipient || prefilterEmail || ''
  );
  const [managers, setManagers] = useState([]);
  const [currentManager, setCurrentManager] = useState('0');
  const [editMode, setEditMode] = useState('structured');
  const [requirements, setRequirements] = useState(savedRequirements);
  const [showUsageCrosspostingAlert, setShowUsageCrosspostingAlert] = useState(false);
  const [formProgress, setFormProgress] = useState(0);

  // ── NEW: client company selection (admin only) ──
  const [selectedClientCompany, setSelectedClientCompany] = useState(
    campaignData?.client_company_id
      ? {
          id: campaignData.client_company_id,
          name: campaignData.client_company_name,
          phphref: campaignData.client_company_photo,
        }
      : null
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [myTeamData, companyUsersResponse] = await Promise.all([
          client.users.fetchMyTeam(),
          client.companies.listUsers(),
        ]);

        const companyUsers = Array.isArray(companyUsersResponse) ? companyUsersResponse : [];
        const teams = Array.isArray(myTeamData?.teams) ? myTeamData.teams : [];
        const { accountManagerIds, campaignManagerIds, teamMembersById } = collectRoleEligibleIds(teams);
        const usersById = new Map(companyUsers.map((u) => [u.id, u]));
        const eligibleIds = new Set([...accountManagerIds, ...campaignManagerIds]);

        const teamEligibleUsers = [...eligibleIds]
          .map((userId) => usersById.get(userId) || teamMembersById.get(userId))
          .filter(Boolean);

        const adminUsers = companyUsers.filter(
          (u) => (u?.type || '').toLowerCase() === 'admin'
        );

        const mergedEligibleUsersById = new Map();
        [...teamEligibleUsers, ...adminUsers].forEach((u) => {
          if (!u?.id) return;
          const full = usersById.get(u.id) || u;
          mergedEligibleUsersById.set(u.id, full);
        });

        const filteredManagers = [...mergedEligibleUsersById.values()].map((u) => ({
          ...u,
          managerRoles: buildManagerRoles(u, accountManagerIds, campaignManagerIds),
        }));

        const fallbackManagers = companyUsers.map((u) => ({
          ...u,
          managerRoles: buildManagerRoles(u, accountManagerIds, campaignManagerIds),
        }));

        const nextManagers = filteredManagers.length > 0 ? filteredManagers : fallbackManagers;
        setManagers(nextManagers);

        if (campaignData?.manager?.email) {
          const savedManagerIndex = nextManagers.findIndex(
            (u) => u?.email === campaignData.manager.email
          );
          if (savedManagerIndex >= 0) {
            setCurrentManager(String(savedManagerIndex));
            return;
          }
        }

        setCurrentManager(nextManagers.length > 0 ? '0' : '');
      } catch (error) {
        setManagers([]);
        setCurrentManager('');
      }
    };
    fetchData();
  }, [campaignData]);

  useEffect(() => {
    if (!campaignData) return;

    setCampaignName(campaignData.campaignName || '');
    setEmailRecipient(campaignData.emailRecipient || prefilterEmail || '');
    setRequirements(parseStructuredBrief(campaignData.campaignBrief));
    setSelectedClientCompany(
      campaignData.client_company_id
        ? {
            id: campaignData.client_company_id,
            name: campaignData.client_company_name,
            phphref: campaignData.client_company_photo,
          }
        : null
    );
  }, [campaignData, prefilterEmail]);

  // Progress calculation
  useEffect(() => {
    let progress = 0;
    if (campaignName.trim()) progress += 20;
    if (emailRecipient.trim()) progress += 20;
    if (requirements.creativeBrief.trim()) progress += 20;
    if (requirements.usage) progress += 20;
    // Admin must pick a company; non-admin gets the 20% free
    if (isAdmin ? selectedClientCompany : true) progress += 20;
    setFormProgress(progress);
  }, [campaignName, emailRecipient, requirements, selectedClientCompany, isAdmin]);

  const handleUsageChange = (event) => {
    setRequirements((prev) => ({ ...prev, usage: event.target.value }));
    setShowUsageCrosspostingAlert(true);
  };

  const handleCreativeChange = (event) => {
    setRequirements((prev) => ({ ...prev, creativeBrief: event.target.value }));
  };

  const convertToText = () =>
    `# Creative Brief\n${requirements.creativeBrief}\n\n# Usage Requirements\nUsage Duration: ${requirements.usage} days\n\nThis campaign brief was automatically generated from your structured inputs.`;

  const handleNext = () => {
    const managerIndex = Number.parseInt(currentManager);
    const campaignData = {
      campaignName,
      campaignBrief: editMode === 'structured' ? convertToText() : '',
      emailRecipient,
      manager: managers[managerIndex]
        ? {
            name: `${managers[managerIndex].first_name} ${managers[managerIndex].last_name}`,
            email: managers[managerIndex].email,
          }
        : null,
      // ── NEW: pass client company to parent ──
      client_company_id: selectedClientCompany?.id || null,
      client_company_name: selectedClientCompany?.name || null,
      client_company_photo: selectedClientCompany?.phphref || null,
    };
    setCampaignData(campaignData);
    onNext();
  };

  // Admins must pick a company; non-admins just need name + email
  const isFormValid =
    campaignName.trim() !== '' &&
    emailRecipient.trim() !== '' &&
    (!isAdmin || !!selectedClientCompany);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background orbs */}
      <Box
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          overflow: 'hidden', zIndex: 0, pointerEvents: 'none',
          '&::before': {
            content: '""', position: 'absolute', top: '10%', right: '15%',
            width: '400px', height: '400px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            animation: 'pulse 8s ease-in-out infinite',
          },
          '&::after': {
            content: '""', position: 'absolute', bottom: '15%', left: '10%',
            width: '300px', height: '300px', borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
            animation: 'pulse 6s ease-in-out infinite reverse',
          },
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1) translateY(0px)', opacity: 0.5 },
            '50%': { transform: 'scale(1.1) translateY(-10px)', opacity: 0.8 },
          },
        }}
      />

      <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 2, md: 4 } }}>
        {/* Progress bar */}
        <Fade in timeout={500}>
          <Card
            sx={{
              mb: 4, borderRadius: 3,
              background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutoAwesomeIcon sx={{ mr: 2, color: '#667eea' }} />
                <Typography variant="h6" fontWeight={600}>Campaign Setup Progress</Typography>
                <Chip label={`${formProgress}%`} size="small" color="primary" sx={{ ml: 'auto', fontWeight: 600 }} />
              </Box>
              <LinearProgress
                variant="determinate"
                value={formProgress}
                sx={{
                  height: 8, borderRadius: 4,
                  backgroundColor: 'rgba(103,126,234,0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                  },
                }}
              />
            </CardContent>
          </Card>
        </Fade>

        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {/* Header */}
          <Fade in timeout={800}>
            <Card
              elevation={0}
              sx={{
                mb: 4,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.9) 100%)',
                backdropFilter: 'blur(30px)', borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.1)', overflow: 'visible', position: 'relative',
                '&::before': {
                  content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '16px 16px 0 0',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 10px 30px rgba(102,126,234,0.3)' }}>
                    <CampaignIcon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h3" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' }, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                      Campaign Details
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                      Configure your campaign information and creative requirements
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Fade>

          <Grid container spacing={4}>

            {/* ── ADMIN ONLY: Client Company Selector ────────────────────────── */}
            {isAdmin && (
              <Grid item xs={12}>
                <Zoom in timeout={500}>
                  <Card
                    elevation={0}
                    sx={{
                      background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)',
                      border: `2px solid ${selectedClientCompany ? 'rgba(103,126,234,0.4)' : 'rgba(255,152,0,0.3)'}`,
                      borderRadius: 4, boxShadow: '0 15px 35px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 25px 50px rgba(0,0,0,0.12)' },
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 2, background: 'linear-gradient(135deg, rgba(103,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' }}>
                          <BusinessIcon sx={{ color: '#667eea' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h5" fontWeight={700} color="#2c3e50">
                              Client Company
                            </Typography>
                            <Chip label="Admin" size="small" color="secondary" sx={{ fontWeight: 700, fontSize: '0.65rem' }} />
                            <Chip label="Required" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            Select the company this campaign is being run for — shows on the ZapFeed
                          </Typography>
                        </Box>
                        {selectedClientCompany && (
                          <Chip icon={<CheckCircleIcon />} label="Selected" color="success" variant="filled" />
                        )}
                      </Stack>

                      <CompanySearchField
                        value={selectedClientCompany}
                        onChange={setSelectedClientCompany}
                      />

                      {/* Preview of selected company */}
                      {selectedClientCompany && (
                        <Slide in direction="up" timeout={300}>
                          <Box
                            sx={{
                              mt: 2, p: 2, borderRadius: 2,
                              background: 'linear-gradient(135deg, rgba(103,126,234,0.06) 0%, rgba(118,75,162,0.06) 100%)',
                              border: '1px solid rgba(103,126,234,0.15)',
                              display: 'flex', alignItems: 'center', gap: 2,
                            }}
                          >
                            <Avatar
                              src={selectedClientCompany.phphref || ''}
                              alt={selectedClientCompany.name}
                              sx={{ width: 44, height: 44, bgcolor: '#667eea', fontWeight: 700 }}
                            >
                              {selectedClientCompany.name?.[0]?.toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography fontWeight={700}>{selectedClientCompany.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {selectedClientCompany.id}
                                {selectedClientCompany.blitzpay_account ? ' · BlitzPay Account' : ''}
                                {selectedClientCompany.account_status ? ` · ${selectedClientCompany.account_status}` : ''}
                              </Typography>
                            </Box>
                            <Chip
                              label={selectedClientCompany.blitzpay_account ? '⚡ BlitzPay' : 'Standard'}
                              size="small"
                              color={selectedClientCompany.blitzpay_account ? 'error' : 'default'}
                              variant={selectedClientCompany.blitzpay_account ? 'filled' : 'outlined'}
                            />
                          </Box>
                        </Slide>
                      )}

                      {!selectedClientCompany && (
                        <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                          A client company must be selected. This determines what appears on the ZapFeed and links the campaign to the correct account.
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            )}

            {/* Campaign Name */}
            <Grid item xs={12}>
              <Zoom in timeout={600}>
                <Card elevation={0} sx={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, boxShadow: '0 15px 35px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 25px 50px rgba(0,0,0,0.12)' } }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 2, background: 'linear-gradient(135deg, rgba(103,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' }}>
                        <CampaignIcon sx={{ color: '#667eea' }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="#2c3e50">Campaign Information</Typography>
                        <Typography variant="body2" color="text.secondary">Give your campaign a memorable name</Typography>
                      </Box>
                      {campaignName.trim() && <Chip icon={<CheckCircleIcon />} label="Complete" color="success" variant="filled" sx={{ ml: 'auto' }} />}
                    </Stack>
                    <TextField
                      label="Campaign Name" variant="outlined" fullWidth
                      value={campaignName} onChange={(e) => setCampaignName(e.target.value)}
                      required placeholder="e.g., Summer 2024 Product Launch"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'white', fontSize: '1.1rem', transition: 'all 0.2s ease', '&:hover': { backgroundColor: 'rgba(103,126,234,0.02)' }, '&.Mui-focused': { backgroundColor: 'rgba(103,126,234,0.05)', '& fieldset': { borderColor: '#667eea', borderWidth: '2px' } } }, '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' } }}
                    />
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>

            {/* Campaign Brief */}
            <Grid item xs={12}>
              <Zoom in timeout={800}>
                <Card elevation={0} sx={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, boxShadow: '0 15px 35px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 25px 50px rgba(0,0,0,0.12)' } }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ mb: 4 }} spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 2, background: 'linear-gradient(135deg, rgba(103,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' }}>
                          <EditIcon sx={{ color: '#667eea' }} />
                        </Box>
                        <Box>
                          <Typography variant="h5" fontWeight={700} color="#2c3e50">Campaign Brief</Typography>
                          <Typography variant="body2" color="text.secondary">Define your creative requirements and usage terms</Typography>
                        </Box>
                      </Stack>
                      <Card sx={{ background: editMode === 'structured' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%)', color: editMode === 'structured' ? 'white' : '#64748b', borderRadius: 3, border: 'none' }}>
                        <CardContent sx={{ p: 2 }}>
                          <FormControlLabel
                            control={<Switch checked={editMode === 'structured'} onChange={(e) => setEditMode(e.target.checked ? 'structured' : 'simple')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.3)' } }} />}
                            label={<Stack alignItems="center" spacing={1}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LightbulbIcon sx={{ fontSize: 20 }} /><Typography variant="body2" fontWeight={600}>Smart Editor</Typography></Box><Typography variant="caption" sx={{ opacity: 0.8 }}>{editMode === 'structured' ? 'Guided Setup' : 'Simple Text'}</Typography></Stack>}
                            labelPlacement="top"
                          />
                        </CardContent>
                      </Card>
                    </Stack>

                    {editMode === 'structured' ? (
                      <Stack spacing={4}>
                        <TextField fullWidth multiline minRows={6} label="Creative Brief" value={requirements.creativeBrief} onChange={handleCreativeChange} placeholder="Describe your creative vision, target audience, key messages, brand guidelines, content style, deliverables, and any specific requirements..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'white', fontSize: '1rem', lineHeight: 1.6, transition: 'all 0.2s ease', '&:hover': { backgroundColor: 'rgba(103,126,234,0.02)' }, '&.Mui-focused': { backgroundColor: 'rgba(103,126,234,0.05)', '& fieldset': { borderColor: '#667eea', borderWidth: '2px' } } }, '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' } }} />
                        <FormControl fullWidth>
                          <InputLabel sx={{ '&.Mui-focused': { color: '#667eea' } }}>Usage Duration</InputLabel>
                          <Select value={requirements.usage} onChange={handleUsageChange} label="Usage Duration" sx={{ borderRadius: 3, backgroundColor: 'white', '&.Mui-focused': { backgroundColor: 'rgba(103,126,234,0.05)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea', borderWidth: '2px' } } }}>
                            {usageDurations.map((d) => (
                              <MenuItem key={d.value} value={d.value}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                  <Typography sx={{ fontSize: '1.2rem' }}>{d.icon}</Typography>
                                  <Typography fontWeight={500}>{d.label}</Typography>
                                </Stack>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {requirements.usage && (
                          <Slide in direction="up" timeout={500}>
                            <Alert severity="success" sx={{ borderRadius: 3 }} icon={<SecurityIcon />}>
                              <AlertTitle>Usage Rights Configured</AlertTitle>
                              Content usage is set for <strong>{requirements.usage} days</strong> from the posting date.
                            </Alert>
                          </Slide>
                        )}
                      </Stack>
                    ) : (
                      <Paper sx={{ p: 4, borderRadius: 3, background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%)', border: '1px solid rgba(103,126,234,0.1)' }}>
                        <TextField label="Campaign Brief Preview" variant="outlined" fullWidth multiline minRows={8} value={convertToText()} disabled helperText="Switch to Smart Editor to make changes" sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'white' } }} />
                      </Paper>
                    )}
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>

            {/* Contact + Manager */}
            <Grid item xs={12} md={userCompany === 'TCC' && managers?.length > 0 ? 6 : 12}>
              <Zoom in timeout={1000}>
                <Card elevation={0} sx={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, boxShadow: '0 15px 35px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', height: '100%', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 25px 50px rgba(0,0,0,0.12)' } }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 2, background: 'linear-gradient(135deg, rgba(103,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' }}>
                        <EmailIcon sx={{ color: '#667eea' }} />
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="#2c3e50">Contact Information</Typography>
                        <Typography variant="body2" color="text.secondary">Where should we send updates?</Typography>
                      </Box>
                      {emailRecipient.trim() && <Chip icon={<CheckCircleIcon />} label="Complete" color="success" variant="filled" sx={{ ml: 'auto' }} />}
                    </Stack>
                    <TextField
                      label="Email Recipient" variant="outlined" fullWidth
                      value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)}
                      required type="email" placeholder="Enter email for campaign notifications"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, backgroundColor: 'white', fontSize: '1.1rem', transition: 'all 0.2s ease', '&:hover': { backgroundColor: 'rgba(103,126,234,0.02)' }, '&.Mui-focused': { backgroundColor: 'rgba(103,126,234,0.05)', '& fieldset': { borderColor: '#667eea', borderWidth: '2px' } } }, '& .MuiInputLabel-root.Mui-focused': { color: '#667eea' } }}
                    />
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>

            {userCompany === 'TCC' && managers?.length > 0 && (
              <Grid item xs={12} md={6}>
                <Zoom in timeout={1200}>
                  <Card elevation={0} sx={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, boxShadow: '0 15px 35px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', height: '100%', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 25px 50px rgba(0,0,0,0.12)' } }}>
                    <CardContent sx={{ p: 4 }}>
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 2, background: 'linear-gradient(135deg, rgba(103,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' }}>
                          <PersonIcon sx={{ color: '#667eea' }} />
                        </Box>
                        <Box>
                          <Typography variant="h5" fontWeight={700} color="#2c3e50">Campaign Manager</Typography>
                          <Typography variant="body2" color="text.secondary">Assign a team member</Typography>
                        </Box>
                      </Stack>
                      <FormControl fullWidth>
                        <InputLabel sx={{ '&.Mui-focused': { color: '#667eea' } }}>Select Manager</InputLabel>
                        <Select value={currentManager} onChange={(e) => setCurrentManager(e.target.value)} label="Select Manager" sx={{ borderRadius: 3, backgroundColor: 'white', '&.Mui-focused': { backgroundColor: 'rgba(103,126,234,0.05)', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea', borderWidth: '2px' } } }}>
                          {managers.map((manager, index) => (
                            <MenuItem value={index} key={index}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ width: 32, height: 32, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                                  {manager.first_name?.charAt(0)}{manager.last_name?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <span>{manager.first_name} {manager.last_name}</span>
                                    {(manager.managerRoles || []).map((role) => (
                                      <Chip key={`${manager.id}-${role}`} label={role} size="small" color={role === 'ADMIN' ? 'secondary' : 'primary'} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                                    ))}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">{manager.email || manager.company_name || 'No email'}</Typography>
                                </Box>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            )}

            {/* Navigation */}
            <Grid item xs={12}>
              <Zoom in timeout={1400}>
                <Card elevation={0} sx={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, boxShadow: '0 15px 35px rgba(0,0,0,0.08)' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={3}>
                      <Button variant="outlined" onClick={onBack} size="large" startIcon={<ArrowBackIcon />} sx={{ borderRadius: 3, borderColor: 'rgba(103,126,234,0.3)', color: '#667eea', fontWeight: 600, py: 1.5, px: 4, minWidth: { xs: '100%', sm: 160 }, transition: 'all 0.3s ease', '&:hover': { borderColor: '#667eea', backgroundColor: 'rgba(103,126,234,0.05)', transform: 'translateY(-2px)', boxShadow: '0 10px 25px rgba(103,126,234,0.15)' } }}>
                        Previous
                      </Button>

                      <Box sx={{ textAlign: 'center', flex: 1 }}>
                        {!isFormValid && (
                          <Alert severity="warning" sx={{ display: 'inline-flex', borderRadius: 2 }}>
                            {isAdmin && !selectedClientCompany
                              ? 'Select a client company to continue'
                              : 'Please complete all required fields to continue'}
                          </Alert>
                        )}
                      </Box>

                      <Button variant="contained" onClick={handleNext} disabled={!isFormValid} size="large" endIcon={<ArrowForwardIcon />}
                        sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 700, py: 1.5, px: 4, fontSize: '1.1rem', minWidth: { xs: '100%', sm: 160 }, boxShadow: '0 8px 25px rgba(102,126,234,0.3)', transition: 'all 0.3s ease', '&:hover': { background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)', transform: 'translateY(-3px)', boxShadow: '0 15px 35px rgba(102,126,234,0.4)' }, '&:disabled': { background: 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)', transform: 'none', boxShadow: 'none' } }}>
                        Continue Setup
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Usage Alert Dialog */}
      <Dialog open={showUsageCrosspostingAlert} onClose={() => setShowUsageCrosspostingAlert(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(20px)' } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: 'white', borderRadius: '16px 16px 0 0', p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <WarningIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>Important Pricing Reminder</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>Ensure your creator pricing reflects all requirements</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              <AlertTitle>Creator Offer Alignment Required</AlertTitle>
              Your usage rights settings and crossposting requirements should be reflected in your creator pricing.
            </Alert>
            <Paper sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%)', border: '1px solid rgba(103,126,234,0.1)' }}>
              <Typography variant="body1" sx={{ mb: 2 }}><strong>What this means:</strong></Typography>
              <Stack spacing={1} sx={{ pl: 2 }}>
                <Typography variant="body2">• Usage rights fees should be included in your base pricing</Typography>
                <Typography variant="body2">• Crossposting requirements may affect creator rates</Typography>
                <Typography variant="body2">• Creators may negotiate if pricing doesn't match requirements</Typography>
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setShowUsageCrosspostingAlert(false)} variant="contained" size="large" fullWidth sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 600, py: 1.5 }}>
            Got it, I'll review pricing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignDetails;
